import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useMutation } from 'react-query'
import DataSource from 'devextreme/data/data_source'
import CustomStore from 'devextreme/data/custom_store'
import { SelectBox } from 'devextreme-react/select-box'
import LoadingButton from '@mui/lab/LoadingButton'
import Typography from '@mui/material/Typography'
import Stack from '@mui/material/Stack'
import Backdrop from '@mui/material/Backdrop'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import Button from '@mui/material/Button'
import Alert from '@mui/material/Alert'
import AlertTitle from '@mui/material/AlertTitle'
import Collapse from '@mui/material/Collapse'
import IconButton from '@mui/material/IconButton'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import List from '@mui/material/List'
import ListItem from '@mui/material/ListItem'
import ListItemText from '@mui/material/ListItemText'
import Divider from '@mui/material/Divider'
import Div from '@jumbo/shared/Div'
import { useJumboDialog } from '@jumbo/components/JumboDialog/hooks/useJumboDialog'
import customerServices from 'app/services/pages/reports/registrations/CustomerServices/customerServices'
import utilServices from 'app/services/util-services'

const getErrorMessage = (error) => {
    if (typeof error === 'string') return error
    if (error?.detail) return error.detail
    if (error?.message) return error.message
    return 'Merge failed. Please try again.'
}

const CustomerMergeForm = ({ onRefresh, handleError, handleSuccess }) => {
    const { hideDialog } = useJumboDialog()
    const [keepCustomerId, setKeepCustomerId] = useState('')
    const [removeCustomerId, setRemoveCustomerId] = useState('')
    const [preview, setPreview] = useState(null)
    const [showRemoveInfo, setShowRemoveInfo] = useState(false)
    const [loadingPreview, setLoadingPreview] = useState(false)
    const [showConfirm, setShowConfirm] = useState(false)
    const [mergeError, setMergeError] = useState(null)

    const customerLookupList = useCallback(
        async (searchTerm) => {
            try {
                return await customerServices.getCustomerLookupList({ firstName: searchTerm })
            } catch (error) {
                handleError(error)
                return []
            }
        },
        [handleError]
    )

    const customerLookupById = useCallback(
        async (key) => {
            try {
                return await customerServices.getCustomerLookupById(key)
            } catch (error) {
                handleError(error)
            }
        },
        [handleError]
    )

    const customerLookupStoreKeep = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => customerLookupList(loadOptions.searchValue ?? ''),
                    byKey: (key) => {
                        if (key === '') return utilServices.emptyList()
                        return customerLookupById(key)
                    },
                }),
                reshapeOnPush: true,
            }),
        [customerLookupList, customerLookupById]
    )

    const customerLookupStoreRemove = useMemo(
        () =>
            new DataSource({
                store: new CustomStore({
                    key: 'id',
                    load: async (loadOptions) => customerLookupList(loadOptions.searchValue ?? ''),
                    byKey: (key) => {
                        if (key === '') return utilServices.emptyList()
                        return customerLookupById(key)
                    },
                }),
                reshapeOnPush: true,
            }),
        [customerLookupList, customerLookupById]
    )

    const customerDisplayText = (data) => {
        if (!data) return ''
        const name = `${data.firstName ?? ''} ${data.lastName ?? ''}`.trim()
        const email = data.email ? ` · ${data.email}` : ''
        const family = data.familyName ? ` · ${data.familyName}` : ''
        return `${name}${email}${family}`
    }

    const selectBoxProps = {
        searchEnabled: true,
        showClearButton: true,
        width: '100%',
        valueExpr: 'id',
        displayExpr: (data) => customerDisplayText(data),
        itemTemplate: (data) => customerDisplayText(data),
        dropDownOptions: {
            maxHeight: 280,
            container: typeof document !== 'undefined' ? document.body : undefined,
        },
    }

    useEffect(() => {
        if (!keepCustomerId || !removeCustomerId) {
            setPreview(null)
            return
        }

        let cancelled = false
        const loadPreview = async () => {
            setLoadingPreview(true)
            try {
                const result = await customerServices.getMergePreview(keepCustomerId, removeCustomerId)
                if (!cancelled) setPreview(result)
            } catch (error) {
                if (!cancelled) {
                    setPreview(null)
                    handleError(error)
                }
            } finally {
                if (!cancelled) setLoadingPreview(false)
            }
        }

        const timer = setTimeout(loadPreview, 300)
        return () => {
            cancelled = true
            clearTimeout(timer)
        }
    }, [keepCustomerId, removeCustomerId, handleError])

    const mergeMutation = useMutation({
        mutationFn: (payload) => customerServices.mergeCustomers(payload),
        onSuccess: (data) => {
            setShowConfirm(false)
            setMergeError(null)
            handleSuccess?.(data.auditSummary || 'Customers merged successfully')
            hideDialog()
            onRefresh?.()
        },
        onError: (error) => {
            setShowConfirm(false)
            const message = getErrorMessage(error)
            setMergeError(message)
            handleError(error)
        },
    })

    const isMerging = mergeMutation.isLoading

    const canMerge =
        keepCustomerId &&
        removeCustomerId &&
        preview &&
        preview.blockingErrors?.length === 0 &&
        !loadingPreview &&
        !isMerging

    const confirmMessage = preview
        ? (() => {
              const accountCount = preview.linkedCustomerCount ?? preview.linkedCustomers?.length ?? 1
              const familyPart = preview.sourceFamilyName ? ` and family "${preview.sourceFamilyName}"` : ''
              return `Permanently delete ${accountCount} customer account(s)${familyPart} and merge all data into ${customerDisplayText(preview.keepCustomer)}? This cannot be undone.`
          })()
        : ''

    const onMergeClick = () => {
        if (!canMerge) return
        setMergeError(null)
        setShowConfirm(true)
    }

    const onConfirmMerge = () => {
        setShowConfirm(false)
        mergeMutation.mutate({
            keepCustomerId,
            removeCustomerId,
        })
    }

    return (
        <Div sx={{ width: '100%', maxWidth: '100%', p: 2, boxSizing: 'border-box', position: 'relative' }}>
            <Backdrop
                open={isMerging}
                sx={{
                    position: 'absolute',
                    zIndex: (theme) => theme.zIndex.modal + 1,
                    borderRadius: 1,
                    flexDirection: 'column',
                    gap: 2,
                }}>
                <CircularProgress color='inherit' />
                <Typography variant='body2' color='inherit'>
                    Merging customers…
                </Typography>
            </Backdrop>

            <Stack spacing={2.5} sx={{ opacity: isMerging ? 0.6 : 1, pointerEvents: isMerging ? 'none' : 'auto' }}>
                <Div sx={{ width: '100%' }}>
                    <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                        Keep this customer
                    </Typography>
                    <SelectBox
                        {...selectBoxProps}
                        dataSource={customerLookupStoreKeep}
                        value={keepCustomerId}
                        onValueChange={setKeepCustomerId}
                        placeholder='Search customer to keep'
                        disabled={isMerging}
                    />
                </Div>

                <Div sx={{ width: '100%' }}>
                    <Stack direction='row' alignItems='center' spacing={0.5}>
                        <Typography variant='subtitle2' sx={{ flex: 1 }}>
                            Remove and Merge
                        </Typography>
                        {removeCustomerId && !isMerging && (
                            <IconButton size='small' onClick={() => setShowRemoveInfo((v) => !v)} aria-label='Family details'>
                                <InfoOutlinedIcon fontSize='small' />
                            </IconButton>
                        )}
                    </Stack>
                    <SelectBox
                        {...selectBoxProps}
                        dataSource={customerLookupStoreRemove}
                        value={removeCustomerId}
                        onValueChange={setRemoveCustomerId}
                        placeholder='Search customer to remove'
                        disabled={isMerging}
                    />
                </Div>

                <Collapse in={showRemoveInfo && !!preview}>
                    <Alert severity='info' sx={{ mb: 1 }}>
                        <AlertTitle>Source family impact — {preview?.sourceFamilyName}</AlertTitle>
                        {preview?.targetFamilyName && (
                            <Typography variant='body2' sx={{ mb: 1 }}>
                                Target family after merge: <strong>{preview.targetFamilyName}</strong>
                            </Typography>
                        )}
                        {(preview?.totalEnrolments > 0 ||
                            preview?.totalTransactions > 0 ||
                            preview?.totalPayments > 0) && (
                            <Typography variant='body2' sx={{ mb: 1 }}>
                                Reassigned to kept customer: {preview.totalEnrolments} enrolment(s),{' '}
                                {preview.totalTransactions} transaction(s), {preview.totalPayments} payment(s).
                                Balance consolidated: {preview.totalBalanceToConsolidate?.toLocaleString?.('en-AU', {
                                    style: 'currency',
                                    currency: 'AUD',
                                }) ?? preview.totalBalanceToConsolidate}
                            </Typography>
                        )}
                        <Typography variant='subtitle2' sx={{ mt: 1, mb: 0.5 }}>
                            Customer accounts to delete
                        </Typography>
                        <List dense disablePadding>
                            {preview?.linkedCustomers?.map((c) => (
                                <ListItem key={c.customerId} disableGutters>
                                    <ListItemText
                                        primary={
                                            <>
                                                {c.firstName} {c.lastName}
                                                {c.isRemoveCustomer ? ' (selected Remove customer)' : ''}
                                            </>
                                        }
                                        secondary={`${c.email ?? ''} · ${c.enrolmentCount} enrolments · ${c.transactionCount} transactions · ${c.paymentCount} payments · Balance ${c.balance}`}
                                    />
                                </ListItem>
                            ))}
                        </List>
                        {preview?.members?.length > 0 && (
                            <>
                                <Divider sx={{ my: 1 }} />
                                <Typography variant='subtitle2' sx={{ mb: 0.5 }}>
                                    All family members
                                </Typography>
                                <List dense disablePadding>
                                    {preview.members.map((m) => (
                                        <ListItem key={m.memberId} disableGutters>
                                            <ListItemText
                                                primary={`${m.firstName} ${m.lastName}${m.relationshipType ? ` (${m.relationshipType})` : ''}`}
                                                secondary={m.actionDescription}
                                            />
                                        </ListItem>
                                    ))}
                                </List>
                            </>
                        )}
                    </Alert>
                </Collapse>

                {loadingPreview && (
                    <Typography variant='body2' color='text.secondary'>
                        Loading merge preview…
                    </Typography>
                )}

                {mergeError && (
                    <Alert severity='error' onClose={() => setMergeError(null)}>
                        {mergeError}
                    </Alert>
                )}

                {preview?.blockingErrors?.length > 0 && (
                    <Alert severity='error'>
                        {preview.blockingErrors.map((msg) => (
                            <Typography key={msg} variant='body2'>
                                {msg}
                            </Typography>
                        ))}
                    </Alert>
                )}

                {preview?.warnings?.length > 0 && preview.blockingErrors?.length === 0 && (
                    <Alert severity='warning'>
                        <AlertTitle>Merge impact</AlertTitle>
                        {preview.warnings.map((msg) => (
                            <Typography key={msg} variant='body2' sx={{ mb: 0.5 }}>
                                {msg}
                            </Typography>
                        ))}
                    </Alert>
                )}

                <Stack direction='row' spacing={2} justifyContent='flex-end' sx={{ pt: 1 }}>
                    <LoadingButton variant='outlined' onClick={hideDialog} disabled={isMerging}>
                        Cancel
                    </LoadingButton>
                    <LoadingButton
                        variant='contained'
                        color='warning'
                        disabled={!canMerge}
                        loading={isMerging}
                        onClick={onMergeClick}>
                        Merge
                    </LoadingButton>
                </Stack>
            </Stack>

            <Dialog
                open={showConfirm}
                onClose={() => !isMerging && setShowConfirm(false)}
                maxWidth='xs'
                fullWidth
                disableEscapeKeyDown={isMerging}>
                <DialogTitle>Confirm merge</DialogTitle>
                <DialogContent>
                    <Typography variant='body2'>{confirmMessage}</Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirm(false)} disabled={isMerging}>
                        No
                    </Button>
                    <LoadingButton variant='contained' color='warning' onClick={onConfirmMerge} loading={isMerging}>
                        Yes, merge
                    </LoadingButton>
                </DialogActions>
            </Dialog>
        </Div>
    )
}

export default CustomerMergeForm
